// Filter Toolbar Component
// Date: December 4, 2025
// Purpose: Search, filter, and sort toolbar for card views

import React from 'react';
import { 
  MagnifyingGlassIcon,
  FunnelIcon,
  BarsArrowUpIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';

export interface FilterOption {
  id: string;
  label: string;
  value: string;
  count?: number;
}

export interface SortOption {
  id: string;
  label: string;
  value: string;
}

export interface FilterToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  quickFilters?: FilterOption[];
  quickFilterGroups?: Array<{
    id: string;
    label: string;
    filters: FilterOption[];
  }>;
  activeFilters?: string[];
  onFilterToggle?: (filterId: string) => void;
  sortOptions?: SortOption[];
  sortValue?: string;
  onSortChange?: (sortId: string) => void;
  showFilters?: boolean;
  showSort?: boolean;
  className?: string;
  filterColorResolver?: (filter: FilterOption, isActive: boolean) => string | undefined;
  /** Optional controls rendered at the start of the row, before the search bar (e.g. roadmap dropdown) */
  controlsBeforeSearch?: React.ReactNode;
  /** On-demand Filters control — sits immediately after Search. */
  toolbarAfterSearch?: React.ReactNode;
  /** Optional controls rendered next to the sort dropdown */
  controlsBeforeSort?: React.ReactNode;
  /** Optional controls rendered after the sort dropdown (e.g. view switcher table/cards) */
  controlsAfterSort?: React.ReactNode;
}

export const FilterToolbar: React.FC<FilterToolbarProps> = ({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  quickFilters = [],
  quickFilterGroups = [],
  activeFilters = [],
  onFilterToggle,
  sortOptions = [],
  sortValue,
  onSortChange,
  showFilters = true,
  showSort = true,
  className = '',
  filterColorResolver,
  controlsBeforeSearch,
  toolbarAfterSearch,
  controlsBeforeSort,
  controlsAfterSort
}) => {
  const handleClearFilters = () => {
    onSearchChange('');
    activeFilters.forEach(filterId => {
      if (onFilterToggle) {
        onFilterToggle(filterId);
      }
    });
  };

  const hasActiveFilters = searchValue.trim() !== '' || activeFilters.length > 0;
  const hasGroupedFilters = quickFilterGroups.length > 0;
  const defaultFilterClass = (isActive: boolean) =>
    isActive
      ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] border-2 border-[var(--color-accent-text)]'
      : 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] border border-[var(--color-border)] hover:bg-[var(--color-accent-bg)] hover:bg-opacity-20';

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Search and Controls Row */}
      <div className="flex items-center gap-3 flex-wrap">
        {controlsBeforeSearch}

        {/* Search Bar */}
        <div className="flex-1 min-w-[200px] relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--color-secondary-text)] opacity-50" />
          <input
            type="text"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full pl-10 pr-4 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)] focus:border-transparent"
          />
          {searchValue && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--color-secondary-text)] opacity-50 hover:opacity-100"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          )}
        </div>

        {toolbarAfterSearch}

        {controlsBeforeSort}

        {/* Sort Dropdown */}
        {showSort && sortOptions.length > 0 && (
          <div className="relative">
            <select
              value={sortValue || sortOptions[0]?.value}
              onChange={(e) => onSortChange?.(e.target.value)}
              className="px-4 py-2 border border-[var(--color-border)] rounded-lg bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)] focus:border-transparent appearance-none pr-8"
            >
              {sortOptions.map((option) => (
                <option key={option.id} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <BarsArrowUpIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[var(--color-secondary-text)] opacity-50 pointer-events-none" />
          </div>
        )}

        {controlsAfterSort}
      </div>

      {/* Quick Filter Chips */}
      {showFilters && !hasGroupedFilters && quickFilters.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              className="flex items-center gap-2 shrink-0"
            >
              <XMarkIcon className="w-4 h-4" />
              <span>Clear</span>
            </Button>
          )}
          <span className="text-sm text-[var(--color-secondary-text)] opacity-75 flex items-center gap-1">
            <FunnelIcon className="w-4 h-4" />
            Filters:
          </span>
          {quickFilters.map((filter) => {
            const isActive = activeFilters.includes(filter.id);
            return (
              <button
                key={filter.id}
                onClick={() => onFilterToggle?.(filter.id)}
                aria-pressed={isActive}
                title={isActive ? 'Selected (click to clear)' : 'Click to filter'}
                className={`
                  inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-bg)] focus-visible:ring-offset-1
                  ${filterColorResolver?.(filter, isActive) || defaultFilterClass(isActive)}
                `}
              >
                {isActive && <CheckIcon className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />}
                {filter.label}
                {filter.count !== undefined && (
                  <span className={`ml-0.5 ${isActive ? 'opacity-75' : 'opacity-50'}`}>
                    ({filter.count})
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {showFilters && hasGroupedFilters && (
        <div className="flex items-center gap-4 flex-wrap">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              className="flex items-center gap-2 shrink-0"
            >
              <XMarkIcon className="w-4 h-4" />
              <span>Clear</span>
            </Button>
          )}
          <span className="text-sm text-[var(--color-secondary-text)] opacity-75 flex items-center gap-1 shrink-0">
            <FunnelIcon className="w-4 h-4" />
            Filters:
          </span>
          {quickFilterGroups.map((group) => (
            <div
              key={group.id}
              className="flex items-center gap-2 shrink-0"
              role="group"
              aria-label={group.label}
            >
              <span className="text-xs font-medium text-[var(--color-secondary-text)] opacity-80 uppercase tracking-wide whitespace-nowrap">
                {group.label}
              </span>
              <div className="flex flex-wrap gap-1.5">
                {group.filters.map((filter) => {
                  const isActive = activeFilters.includes(filter.id);
                  return (
                    <button
                      key={filter.id}
                      onClick={() => onFilterToggle?.(filter.id)}
                      aria-pressed={isActive}
                      title={isActive ? 'Selected (click to clear)' : 'Click to filter'}
                      className={`
                        inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-bg)] focus-visible:ring-offset-1
                        ${filterColorResolver?.(filter, isActive) || defaultFilterClass(isActive)}
                      `}
                    >
                      {isActive && <CheckIcon className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />}
                      {filter.label}
                      {filter.count !== undefined && (
                        <span className={`ml-0.5 ${isActive ? 'opacity-75' : 'opacity-50'}`}>
                          ({filter.count})
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

